import { Savings } from "@mui/icons-material";
import { Box, Typography, Link } from "@mui/material";
import {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { Loader } from "src/shared/components";
import { TablePage } from "src/shared/components/Table";
import { useGetErpPartnerClientsQuery } from "src/shared/hooks/partnerClientAccumulation/useGetErpPartnerClientsQuery";
import { useGetErpPartnerClientsAccumulationQuery } from "../api/api";
import { IServiceData, IAccountInfo, IRowData } from "../model/types/types";
import { TableHeader } from "./components/TableHeader";

// Подумать над вынесением логики в хуки

export const ErpPartnerClientsAccumulation = () => {
  const { partner_id } = useParams();
  const { query, filters, onFilter, onResetFilter } =
    useGetErpPartnerClientsQuery();
  const { data: partnerClientsData, isFetching } =
    useGetErpPartnerClientsAccumulationQuery(
      { partner_id, ...query },
      {
        skip: !query.start_date || !query.end_date,
      },
    );

  if (isFetching) {
    return <Loader position="absolute" blur />;
  }

  const allMonths = Array.from(
    new Set(
      Object.values(partnerClientsData!.data.items).flatMap((item) =>
        item.accounts_info_list.flatMap((account) =>
          Object.keys(account.period_data),
        ),
      ),
    ),
  ).sort();

  const metricKeys = [
    ["spend", "Открутка Клиента Партнера в валюте аккаунта"],
    ["client_bonus", "Бонус Клиенту в валюте аккаунта"],
    ["client_bonus_percent", "Процент бонуса клиента"],
    ["partner_bonus_by_spend", "Бонус по открутке Партнера в валюте аккаунта"],
    ["partner_bonus_percent", "Процент бонуса партнёра"],
    ["to_pay", "Бонус к выплате Партнеру в валюте аккаунта"],
    ["to_pay_in_partner_currency", "Бонус к выплате партнеру в валюте КДА"],
  ];

  const servicesData = Object.values(partnerClientsData!.data.items).reduce(
    (acc: Record<string, IServiceData>, item) => {
      if (!acc[item.service_name]) {
        acc[item.service_name] = {
          virtual_contract_id: item.virtual_contract_id,
          virtual_contract_number: item.virtual_contract_number,
          mcc: item.mcc,
          virtual_contract_co_name: item.virtual_contract_co_name,
          accounts: [],
        };
      }
      acc[item.service_name].accounts.push(...item.accounts_info_list);

      return acc;
    },
    {} as Record<string, IServiceData>,
  );

  const generateRows = (
    accountsInfoList: IAccountInfo[],
    mcc: string,
  ): IRowData[] => {
    const rows = accountsInfoList.map((account, index) => {
      const row: IRowData = {
        id: `${account.client_id}-${index}`,
        client_id: account.client_id,
        client_name: account.client_name,
        site: account.site,
        account_id: account.account_id,
        mcc,
      };

      allMonths.forEach((month) => {
        const monthData = account.period_data[month];

        row[`${month}_spend`] = monthData.spend_in_service_currency;
        row[`${month}_client_bonus`] =
          monthData.client_bonus_in_service_currency;
        row[`${month}_client_bonus_percent`] = monthData.client_bonus_percent;
        row[`${month}_partner_bonus_by_spend`] =
          monthData.partner_bonus_by_spend_in_service_currency;
        row[`${month}_partner_bonus_percent`] = monthData.partner_bonus_percent;
        row[`${month}_to_pay`] =
          monthData.partner_bonus_to_pay_in_service_currency;
        row[`${month}_to_pay_in_partner_currency`] =
          monthData.partner_bonus_to_pay_in_partner_contract_currency;
        row[`${month}_client_service_currency`] =
          monthData.client_service_currency;
        row[`${month}_partner_service_currency`] =
          monthData.partner_service_currency;
        row[`${month}_partner_contract_currency`] =
          monthData.partner_contract_currency;
      });

      return row;
    });

    if (rows.length > 0) {
      const totalRow: IRowData = {
        id: "total-row",
        client_name: "Итого",
        site: "",
        account_id: "",
      };

      allMonths.forEach((month) => {
        const firstRow = rows[0];
        totalRow[`${month}_spend`] =
          `0 ${firstRow[`${month}_client_service_currency`] || ""}`;
        totalRow[`${month}_client_bonus`] =
          `0 ${firstRow[`${month}_client_service_currency`] || ""}`;
        totalRow[`${month}_partner_bonus_by_spend`] =
          `0 ${firstRow[`${month}_partner_service_currency`] || ""}`;
        totalRow[`${month}_to_pay`] =
          `0 ${firstRow[`${month}_partner_service_currency`] || ""}`;
        totalRow[`${month}_to_pay_in_partner_currency`] =
          `0 ${firstRow[`${month}_partner_contract_currency`] || ""}`;
        totalRow[`${month}_client_bonus_percent`] = "";
        totalRow[`${month}_partner_bonus_percent`] = "";
      });

      rows.forEach((row) => {
        allMonths.forEach((month) => {
          const addToTotal = (field: string) => {
            const value = row[field];
            const total = totalRow[field];

            if (typeof value === "number" && typeof total === "string") {
              const [current, currency] = total.split(" ");
              const updated = parseFloat(current) + value;
              totalRow[field] = `${updated.toFixed(2)} ${currency}`;
            }
          };

          addToTotal(`${month}_spend`);
          addToTotal(`${month}_client_bonus`);
          addToTotal(`${month}_partner_bonus_by_spend`);
          addToTotal(`${month}_to_pay`);
          addToTotal(`${month}_to_pay_in_partner_currency`);
        });
      });

      return [...rows, totalRow];
    }

    return rows;
  };

  const generateTableConfig = () => {
    const columns: GridColDef[] = [
      {
        field: "client_name",
        headerName: "ЮЛ Клиента Партнера",
        minWidth: 250,
        sortable: false,
        headerClassName: "client-info-header",
        renderCell: ({ row }) =>
          row.account_id ? ( // условия для исключения поля "Итого"
            <Link
              href={`/crm/client/${row.client_id}`}
              target="_top"
              underline="hover"
              sx={{
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {row.client_name}
              {console.log(row)}
            </Link>
          ) : (
            row.client_name
          ),
      },
      {
        field: "account_id",
        headerName: "ID Аккаунта/логин",
        flex: 1,
        minWidth: 170,
        sortable: false,
        headerClassName: "client-info-header",
        renderCell: (params: GridRenderCellParams) => {
          const { row, value } = params;

          return (
            <div>
              <div>{value}</div>
              {row.mcc && (
                <div style={{ fontSize: "0.8em", color: "#888" }}>
                  ({row.mcc})
                </div>
              )}
            </div>
          );
        },
      },
      ...allMonths.flatMap((month) =>
        metricKeys.map(([key, label]) => {
          return {
            field: `${month}_${key}`,
            headerName: label,
            flex: 1,
            minWidth: 177,
            type: "number" as const,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
              const row = params.row as IRowData;
              const value = params.value;
              const isTotalRow = row.id === "total-row";

              if (key.includes("percent")) {
                return !isTotalRow && value != null && value !== ""
                  ? `${value}%`
                  : "";
              }

              const currencyFieldMap: Record<string, keyof typeof row> = {
                spend: `${month}_client_service_currency`,
                client_bonus: `${month}_client_service_currency`,
                partner_bonus_by_spend: `${month}_partner_service_currency`,
                to_pay: `${month}_partner_service_currency`,
                to_pay_in_partner_currency: `${month}_partner_contract_currency`,
              };

              const currencyKey = currencyFieldMap[key];
              const currency = row?.[currencyKey] ?? "";

              return value != null ? `${value} ${currency}` : "";
            },
          };
        }),
      ),
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: "Информация по клиенту партнера",
        headerClassName: "client-info-group-header",
        children: ["client_name", "site", "account_id"].map((field) => ({
          field,
        })),
      },
      ...allMonths.map((month) => ({
        groupId: month,
        headerClassName: "centered-header",
        children: metricKeys.map(([key]) => ({ field: `${month}_${key}` })),
      })),
    ];

    return { columns, columnGroupingModel };
  };

  const { columns, columnGroupingModel } = generateTableConfig();

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        paddingTop: 5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
          Партнер:{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {partnerClientsData?.data.partner.name}
          </Box>
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 1,
          }}
        >
          <Savings color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
            Накопления:{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {partnerClientsData?.data.accumulation?.accumulation_sum !==
                undefined &&
              partnerClientsData?.data.accumulation?.accumulation_sum !== null
                ? `${partnerClientsData.data.accumulation.accumulation_sum} ${partnerClientsData.data.accumulation.currency}`
                : "Нет данных"}
            </Box>
          </Typography>
        </Box>
      </Box>

      {Object.entries(servicesData).length > 0 ? (
        Object.entries(servicesData).map(
          ([serviceName, serviceData], index) => (
            <Box key={serviceName}>
              <TablePage
                rowHeight={100}
                columnHeaderHeight={80}
                getRowClassName={(params) =>
                  params.id === "total-row" ? "total-row" : ""
                }
                sx={{
                  "& .total-row": {
                    fontWeight: "bold",
                  },
                  "& .client-info-group-header": {
                    backgroundColor: "rgba(0, 0, 0, 0.20) !important",
                    fontWeight: "bold",
                    fontSize: "1rem",
                  },

                  "& .client-info-header": {
                    backgroundColor: "rgba(0, 0, 0, 0.20) !important",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    borderRight: "2px solid #ddd",
                  },
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    whiteSpace: "normal", // вместо nowrap
                    lineHeight: 1.3,
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
                title={
                  <TableHeader
                    serviceName={serviceName}
                    serviceData={serviceData}
                    index={index}
                  />
                }
                columns={columns}
                tableFilterModel={
                  index === 0
                    ? {
                        onReset: () =>
                          onResetFilter(["month_from", "month_to"]),
                        onFilter,
                        filters,
                      }
                    : undefined
                }
                rows={generateRows(serviceData.accounts, serviceData.mcc)}
                columnGroupingModel={columnGroupingModel}
                slots={{ pagination: null }}
                loading={isFetching}
              />
            </Box>
          ),
        )
      ) : (
        <>
          <TablePage
            columns={columns}
            tableFilterModel={{
              onReset: () => onResetFilter(["month_from", "month_to"]),
              onFilter,
              filters,
            }}
            rows={generateRows([], "")}
            slots={{ pagination: null }}
            localeText={{
              noRowsLabel: "Нет данных за выбранный период",
            }}
          />
        </>
      )}
    </Box>
  );
};
